/**
 * Register page initialization
 */

import { showToast } from "./web-components";

interface OAuthProvider {
  name: string;
  displayName: string;
  authorizeUrl: string;
}

interface PolicyData {
  text: string;
  url: string | null;
}

const PROVIDER_ICONS: Record<string, string> = {
  google:
    '<path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>',
  github:
    '<path d="M12 2C6.477 2 2 6.477 2 12c0 4.42 2.865 8.17 6.839 9.49.5.092.682-.217.682-.482 0-.237-.008-.866-.013-1.7-2.782.603-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0112 6.836c.85.004 1.705.114 2.504.336 1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.203 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.743 0 .267.18.578.688.48C19.138 20.167 22 16.418 22 12c0-5.523-4.477-10-10-10z"/>',
  microsoft:
    '<path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z"/>',
  facebook:
    '<path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>',
  apple:
    '<path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>',
};

const ERROR_MESSAGES: Record<string, string> = {
  oauth_failed: "Social login failed. Please try again.",
  account_exists:
    "An account with this email already exists. Please log in with your password.",
  auto_create_disabled:
    "Account creation is disabled. Please contact your administrator.",
  email_not_verified:
    "Email verification required. Please use an account with a verified email.",
  no_email: "Email address is required for registration.",
};

async function loadOAuthProviders(): Promise<void> {
  try {
    const response = await fetch("/api/auth/oauth/providers");
    if (!response.ok) return;

    const data = (await response.json()) as { providers?: OAuthProvider[] };
    if (!data.providers || data.providers.length === 0) return;

    const oauthSection = document.getElementById("oauthSection");
    const oauthButtons = document.getElementById("oauthButtons");
    if (!oauthSection || !oauthButtons) return;

    oauthSection.classList.remove("hidden");

    data.providers.forEach((provider: OAuthProvider) => {
      const button = document.createElement("button");
      button.type = "button";
      button.className = `oauth-btn oauth-btn-${provider.name}`;
      button.innerHTML = `<svg viewBox="0 0 24 24" fill="currentColor">${PROVIDER_ICONS[provider.name] || ""}</svg><span>Sign up with ${provider.displayName}</span>`;
      button.onclick = () => {
        window.location.href = provider.authorizeUrl;
      };
      oauthButtons.appendChild(button);
    });
  } catch (error) {
    console.error("Failed to load OAuth providers:", error);
  }
}

function handleUrlError(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const urlError = urlParams.get("error");
  if (urlError) {
    showToast({ message: ERROR_MESSAGES[urlError] || urlError, type: "error" });
  }
}

function collectAcceptedPolicies(): PolicyData[] {
  const checkboxes = document.querySelectorAll<HTMLInputElement>(
    ".policy-checkbox:checked"
  );
  const acceptedPolicies: PolicyData[] = [];

  checkboxes.forEach((checkbox) => {
    try {
      const policyData = JSON.parse(checkbox.dataset.policy || "{}");
      acceptedPolicies.push(policyData);
    } catch (error) {
      console.error("Failed to parse policy data:", error);
    }
  });

  return acceptedPolicies;
}

function setupRegisterForm(): void {
  const form = document.getElementById(
    "registerForm"
  ) as HTMLFormElement | null;
  if (!form) return;

  form.addEventListener("submit", async (e) => {
    e.preventDefault();

    const formData = new FormData(form);
    const data: Record<string, unknown> = {};
    formData.forEach((value, key) => {
      data[key] = value.toString();
    });

    const acceptedPolicies = collectAcceptedPolicies();
    if (acceptedPolicies.length > 0) {
      data.acceptedPolicies = acceptedPolicies;
    }

    try {
      const response = await fetch("/api/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const result = (await response.json()) as {
        message?: string;
        error?: string;
      };

      if (response.ok) {
        showToast({ message: "Registration successful! Redirecting to login...", type: "success" });
        setTimeout(() => (window.location.href = "/login"), 1000);
      } else {
        showToast({
          message: result.message || result.error || "Registration failed",
          type: "error",
        });
      }
    } catch {
      showToast({ message: "Network error", type: "error" });
    }
  });
}

export function initRegister(): void {
  handleUrlError();
  loadOAuthProviders();
  setupRegisterForm();
}
